import { fromJS } from 'immutable';
import {
  Auth,
  DocumentSearchQueryParams,
  DocumentSearchResponse,
  DocumentSearchResultType,
  RestDocumentResponse,
  normalizeRestDocument,
} from './DocumentsApi';
import type { PromiseClient } from 'hub-http/adapters/promiseClient';
import { createRpcClientV2 } from 'rpc-client-utils';

import { listEntriesRpc } from '../../../../__generated__/chirp/com/hubspot/sales/documents/rpc/SalesDocumentIndexRpc';
import { mapDocumentEntry } from './mapDocumentEntry';

const DOCUMENTS_URI = 'salescontentsearch/v2/search';
// eslint-disable-next-line no-restricted-syntax
let savedCache: DocumentSearchResponse | null;
// eslint-disable-next-line no-restricted-syntax
let cachedParams: DocumentSearchQueryParams | null;

/**
 * Meant to be used in Unit / Integration tests only
 */
export const bustDocumentsCache = () => {
  savedCache = null;
  cachedParams = null;
};

const getPostBody = ({
  user_id,
  limit = 100,
  offset = 0,
  query = '',
}: { user_id: number | null } & DocumentSearchQueryParams) => {
  const filters = [
    {
      field: 'content_type',
      values: ['DOCUMENT'],
    },
  ];
  if (user_id) {
    filters.push({
      field: 'user_id',
      values: [`${user_id}`],
    });
  }
  return {
    query,
    limit,
    offset,
    filters,
    contentTypesToHydrate: ['DOCUMENT', 'DOCUMENTS_FOLDER'],
  };
};

/**
 *
 * @param {Object} options
 * @param {Object} options.httpClient - an http client containing a `post` method
 * @param {Object} options.cache - HS auth object with that contains a user object with a user_id
 * @returns {Promise}
 */
export function fetchDocuments({
  httpClient,
  auth,
  cache = true,
  queryParams = {},
}: {
  httpClient: PromiseClient;
  auth?: Auth;
  cache?: boolean;
  queryParams?: DocumentSearchQueryParams;
}) {
  let promise: Promise<DocumentSearchResponse>;

  const shouldBustCache =
    cache &&
    savedCache &&
    cachedParams &&
    fromJS(cachedParams).equals(fromJS(queryParams));

  if (shouldBustCache) {
    promise = Promise.resolve(savedCache!);
    promise.catch((err) => {
      setTimeout(() => {
        throw err;
      });
    });
    return promise;
  }

  const user_id = auth ? auth.user.user_id : null;
  promise = httpClient
    .post<RestDocumentResponse>(DOCUMENTS_URI, {
      data: getPostBody({ ...queryParams, ...{ user_id } }),
    })
    .then((res) => {
      const normalized: DocumentSearchResponse = {
        ...res,
        results: res.results.map(normalizeRestDocument),
      };
      savedCache = normalized;
      cachedParams = queryParams;
      return normalized;
    })
    .catch((error) => {
      return Promise.reject(error);
    });

  promise.catch((err) => {
    setTimeout(() => {
      throw err;
    });
  });
  return promise;
}

// eslint-disable-next-line no-restricted-syntax
let savedChirpCache: DocumentSearchResponse | null;
// eslint-disable-next-line no-restricted-syntax
let cachedChirpParams: DocumentSearchQueryParams | null;

export const bustDocumentsChirpCache = () => {
  savedChirpCache = null;
  cachedChirpParams = null;
};

export function fetchDocumentsCHIRP({
  httpClient,
  cache = true,
  queryParams = {},
}: {
  httpClient: PromiseClient;
  cache?: boolean;
  queryParams?: DocumentSearchQueryParams;
}): Promise<DocumentSearchResponse> {
  const { limit = 100, offset = 0 } = queryParams;

  const cacheHit =
    cache &&
    savedChirpCache &&
    cachedChirpParams &&
    fromJS(cachedChirpParams).equals(fromJS(queryParams));

  if (cacheHit) {
    return Promise.resolve(savedChirpCache!);
  }

  const rpcClient = createRpcClientV2({ hubHttpClient: httpClient });
  const promise = rpcClient
    .call(listEntriesRpc, {
      request: {
        entryTypes: ['DOCUMENT'],
        filters: [],
        pageLength: limit,
        offset,
      },
    })
    .then((response) => {
      const results = (response.results ?? [])
        .map(mapDocumentEntry)
        .filter(
          (
            item: DocumentSearchResultType | null
          ): item is DocumentSearchResultType => item !== null
        );
      const mapped: DocumentSearchResponse = {
        total: results.length,
        offset: response.offset,
        results,
      };
      savedChirpCache = mapped;
      cachedChirpParams = queryParams;
      return mapped;
    });

  promise.catch((err) => {
    setTimeout(() => {
      throw err;
    });
  });
  return promise;
}
